import { create } from "zustand";

interface PaymentLoadingState {
  isLoading: boolean;
  paymentFormSubmitting: boolean;
  verifyingRedirectResult: boolean;
  // Count of price-affecting mutations currently in flight (apply/unapply
  // promo code, value card, etc.). While > 0 the Pay CTA must stay disabled
  // because the Adyen checkout session may still be rebuilding server-side
  // and the Drop-in / Apple Pay button may still be primed with stale data.
  pricingMutationsInFlight: number;
  // Set only by redirectWithReturnFallback, when a redirect back to the app was
  // fired on a confirmed payment but the document is still here a moment later.
  // Holds the target URL for the manual return button. Never set from anywhere
  // else: a payment the backend hasn't confirmed must not be able to reveal it.
  returnToAppUrl: string | null;
  setPaymentFormSubmitting: (paymentFormSubmitting: boolean) => void;
  setVerifyingRedirectResult: (verifyingRedirectResult: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  beginPricingMutation: () => void;
  endPricingMutation: () => void;
}

export const usePaymentLoadingStore = create<PaymentLoadingState>(set => ({
  isLoading: false,
  paymentFormSubmitting: false,
  verifyingRedirectResult: false,
  pricingMutationsInFlight: 0,
  returnToAppUrl: null,
  setVerifyingRedirectResult: verifyingRedirectResult =>
    set({ verifyingRedirectResult }),
  setPaymentFormSubmitting: paymentFormSubmitting =>
    set({ paymentFormSubmitting }),
  setIsLoading: isLoading => set({ isLoading }),
  beginPricingMutation: () =>
    set(state => ({
      pricingMutationsInFlight: state.pricingMutationsInFlight + 1,
    })),
  endPricingMutation: () =>
    set(state => ({
      pricingMutationsInFlight: Math.max(0, state.pricingMutationsInFlight - 1),
    })),
}));

/**
 * The checkout is paid and the payer is on their way back to the app.
 *
 * `returnToAppUrl` is only ever set by redirectWithReturnFallback, on a
 * backend-confirmed payment, so this is the one condition that means "the order
 * is settled". Nothing the payer could still submit against it — the Pay CTA,
 * promo codes, value cards — may stay live past this point, so every such
 * control reads it from here rather than assembling its own lock condition.
 */
export const useCheckoutPaid = () =>
  usePaymentLoadingStore(state => !!state.returnToAppUrl);
